import type { MouseEvent, ReactNode } from 'react';
import type {
  ButtonColor,
  ButtonIconPosition,
  ButtonProps,
} from '../../Button/Button';
import type { LinkButtonProps } from '../../LinkButton/LinkButton';

import { DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { EMPTY_ARRAY, EMPTY_FUNCTION } from '../../../lib/globals';
import { getTestProps } from '../../../lib/helpers';

import { CloseIcon } from '../../../images/shapes';

import Button from '../../Button/Button';
import LinkButton from '../../LinkButton/LinkButton';

const sizeClass = {
  sm: 'w-full max-w-[300px]',
  md: 'w-full max-w-[500px]',
  lg: 'w-full max-w-[700px]',
  xl: 'w-full max-w-[900px]',
  '2xl': 'w-full max-w-[1052px] m-1 md:m-4',
  '3xl': 'w-full max-w-[1240px] m-1 md:m-4',
  page:
    'w-full max-w-[calc(100%-4rem)] sm:max-w-[calc(100%-7rem)] lg:max-w-[calc(100%-20rem)] h-screen ' +
    'max-h-screen bg-blue-50 data-closed:translate-x-full data-closed:opacity-0',
  inset: 'w-screen h-screen',
};

type ButtonModalBaseProps = {
  key: string;
  label: ReactNode;
  color?: ButtonColor;
  additionalClasses?: string;
  iconImage?: ReactNode;
  iconPosition?: ButtonIconPosition;
  disabled?: boolean;
  testId?: string;
};

export type ButtonModalProps =
  | (ButtonModalBaseProps & {
      link: string;
      onClick?: LinkButtonProps['onClick'];
      props?: LinkButtonProps;
    })
  | (ButtonModalBaseProps & {
      link?: never;
      onClick?: ButtonProps['onClick'];
      props?: ButtonProps;
    });

export interface ModalPanelProps {
  /**
   * On close callback
   */
  onClose?: (e: MouseEvent<HTMLButtonElement>) => any;
  /**
   * Modal dialog title
   */
  title?: ReactNode;
  /**
   * Modal dialog subtitle
   */
  content?: ReactNode;
  /**
   * Buttons under the dialog text
   */
  buttons?: ButtonModalProps[];
  /**
   * Modal size
   */
  size?: keyof typeof sizeClass;
  /**
   * If close button should be hidden
   */
  hideClose?: boolean;
  /**
   * Modal dialog additional classes
   */
  dialogAdditionalClasses?: string;
  /**
   * Content additional classes
   */
  contentAdditionalClasses?: string;
  /**
   * Modal panel test id
   */
  testId?: string;
}

const ModalPanel = ({
  onClose = EMPTY_FUNCTION,
  buttons = EMPTY_ARRAY,
  title = '',
  content = '',
  size = 'md',
  hideClose = false,
  dialogAdditionalClasses = '',
  contentAdditionalClasses = '',
  testId = '',
}: ModalPanelProps) => {
  const { t } = useTranslation();

  const isFullSize = size === 'page' || size === 'inset';

  return (
    <div
      className="fixed inset-0 overflow-y-auto overflow-y-overlay"
      {...getTestProps(testId, 'container')}
    >
      <DialogBackdrop className="fixed inset-0 bg-slate-700/50" />
      <div
        className={twMerge(
          'flex min-h-full text-center',
          isFullSize ? 'justify-end' : 'items-center justify-center sm:p-0 p-4',
        )}
      >
        <DialogPanel
          className={twMerge(
            'relative bg-white dark:bg-slate-900 text-left ',
            'overflow-hidden shadow-xl text-black',
            'ease-in duration-200 backdrop-blur-none',
            sizeClass[size || 'md'],
            !isFullSize &&
              'max-h-[calc(100vh-5rem)] md:max-h-[calc(100vh-3rem)] rounded-lg ' +
                'data-closed:scale-95 data-closed:opacity-0 data-closed:translate-y-4',
            dialogAdditionalClasses,
          )}
          transition
        >
          {!hideClose && (
            <div className="absolute top-0 right-0 z-20">
              <button
                type="button"
                className="rounded-md text-slate-600 hover:text-slate-500 focus:outline-hidden
                pt-3 pr-3 sm:pt-7 sm:pr-6"
                onClick={onClose}
                {...getTestProps(testId, 'x-icon')}
              >
                <span className="sr-only">{t('Global.CloseModal')}</span>
                <CloseIcon
                  className="w-2.5 sm:w-3.5 h-2.5 sm:h-3.5 text-slate-900 dark:text-white hover:text-slate-300"
                  aria-hidden="true"
                />
              </button>
            </div>
          )}

          <div className="h-full" {...getTestProps(testId, 'body')}>
            {title && (
              <DialogTitle
                className={twMerge(
                  'leading-none flex items-center dark:text-white',
                )}
              >
                <div className="w-full font-medium px-6 md:px-11 py-4 dark:text-white">
                  {title}
                </div>
              </DialogTitle>
            )}
            {content && (
              <div
                className={twMerge(
                  'flex flex-col items-center justify-start',
                  'overflow-y-auto w-full scrollbar-sm',
                  title ? 'pt-3' : 'pt-7',
                  !isFullSize
                    ? 'max-h-[calc(100vh-7rem)] px-6 md:px-11 pb-20'
                    : 'max-h-screen pt-0',
                  contentAdditionalClasses,
                )}
              >
                <div className="w-full h-full dark:text-slate-200">
                  {content}
                </div>
              </div>
            )}
          </div>
          {buttons && buttons.length > 0 && (
            <div
              className="w-full absolute left-0 bottom-0 flex flex-wrap items-center justify-center p-3 gap-2
              border-t border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900"
              {...getTestProps(testId, 'buttons')}
            >
              {buttons.map((button) => {
                const baseProps = {
                  buttonSize: 'base' as const,
                  buttonColor: button.color,
                  additionalClasses: button.additionalClasses,
                  iconImage: button.iconImage,
                  iconPosition: button.iconPosition,
                  disabled: button.disabled,
                  testId: button.testId,
                  children: button.label,
                };

                if (button.link)
                  return (
                    <LinkButton
                      key={button.key}
                      {...baseProps}
                      link={button.link}
                      onClick={button.onClick}
                      {...(button.props as Omit<
                        LinkButtonProps,
                        keyof ButtonModalBaseProps
                      >)}
                    />
                  );

                return (
                  <Button
                    key={button.key}
                    {...baseProps}
                    onClick={button.onClick}
                    {...(button.props as Omit<
                      ButtonProps,
                      keyof ButtonModalBaseProps
                    >)}
                  />
                );
              })}
            </div>
          )}
        </DialogPanel>
      </div>
    </div>
  );
};

export default ModalPanel;
